import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { useGetQueueAlerts, useGetTenantWorkflows } from 'services/hooks/queries';
import { formatQueueDelayAlertSummary } from 'utils/queueDelayAlert.utils';

import styles from './QueueDelayAlertStrip.module.scss';

export type QueueDelayAlertStripProps = {
    /** Both the queue-alerts endpoint and this strip are manager-only; an agent session 403s. */
    isManager: boolean;
};

/**
 * Open queue delay alerts across the tenant (FR-3.1). Renders nothing when every campaign is
 * healthy, so a quiet tenant sees no empty state. Each row links to the workflow's live
 * monitor, which is where the backlog it is describing can actually be worked.
 */
export const QueueDelayAlertStrip: React.FC<QueueDelayAlertStripProps> = ({ isManager }) => {
    const { queueAlerts } = useGetQueueAlerts({ active: true }, isManager);
    const { tenantWorkflows } = useGetTenantWorkflows(isManager);

    const titleByWorkflowId = useMemo(
        () => new Map((tenantWorkflows ?? []).map((workflow) => [workflow.id, workflow.title])),
        [tenantWorkflows]
    );

    if (queueAlerts.length === 0) return null;

    return (
        <section className={styles.strip} aria-label='Queue delay alerts' data-testid='queue-delay-alert-strip'>
            {queueAlerts.map((alert) => (
                <div key={alert.id} className={styles.row}>
                    <TriangleAlert className={styles.icon} size={18} aria-hidden='true' />

                    <div className={styles.body}>
                        <span className={styles.workflow}>
                            {titleByWorkflowId.get(alert.workflowId) ?? 'Queue delay'}
                        </span>
                        <span className={styles.summary}>{formatQueueDelayAlertSummary(alert.payload)}</span>
                    </div>

                    <Link className={styles.action} to={`/agentic-workflow/${alert.workflowId}/live`}>
                        View live monitor
                    </Link>
                </div>
            ))}
        </section>
    );
};

export default QueueDelayAlertStrip;
